import { marketplaces } from '@/data/marketplaces';
import getFilterMarketplaces from '@/helpers/get-filter-marketplaces';
import type { Marketplace } from '@/store/organization/projectSlice';
import type { RootState } from '@/store/store';
import { Dropdown } from 'flowbite-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

interface MarketplaceDropdownProps {
  selectedMarketplace: Marketplace;
  onChangeMarketplace: (marketplace: Marketplace) => void;
  onlyForActiveOrganization?: boolean;
  className?: string;
}

const MarketplaceDropdown: React.FC<MarketplaceDropdownProps> = ({
  selectedMarketplace,
  onChangeMarketplace,
  onlyForActiveOrganization,
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const projects = useSelector((state: RootState) => state.projects.projects);

  const onlyActiveMarketplaces = new Set(
    projects?.map?.(({ marketplace }) => marketplace?.marketplace_type) || []
  );

  const filteredMarketplaces = getFilterMarketplaces()
    .filter(([, { title }]) =>
      title?.toLowerCase?.().includes?.(searchTerm?.toLowerCase?.())
    )
    .filter(
      ([marketplace]) =>
        !onlyForActiveOrganization ||
        onlyActiveMarketplaces.has(marketplace as Marketplace)
    );

  // Focus the input when dropdown opens
  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, []);

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  };

  return (
    <div title="Маркетплейс" ref={dropdownRef} className={className}>
      <Dropdown
        label="marketplaces-dropdown"
        theme={{
          content: 'max-h-[300px] overflow-scroll relative',
          floating: {
            base: 'z-[110]',
          },
        }}
        renderTrigger={() => {
          return (
            <img
              className="h-[42px] w-[42px] shrink-0 cursor-pointer self-center rounded-lg border-2 border-solid border-[#A4CAFE]"
              alt="icon"
              src={marketplaces?.[selectedMarketplace]?.icon}
            />
          );
        }}
      >
        {(filteredMarketplaces?.length > 3 || searchTerm) && (
          <div className="p-2">
            <input
              ref={inputRef}
              type="text"
              className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Поиск маркетплейса..."
              value={searchTerm}
              onChange={handleInputChange}
              onClick={handleInputClick}
              onKeyDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className="max-h-[200px] overflow-y-auto">
          {filteredMarketplaces.map(([marketplace, { title, icon }]) => (
            <Dropdown.Item
              onClick={() => {
                onChangeMarketplace(marketplace as Marketplace);
                setSearchTerm('');
              }}
              key={title}
            >
              <img
                className="mr-2 h-[42px] w-[42px] shrink-0 self-center rounded-lg border-2 border-solid border-[#A4CAFE]"
                alt="icon"
                src={icon}
              />
              {title}
            </Dropdown.Item>
          ))}
          {filteredMarketplaces.length === 0 && (
            <div className="px-4 py-2 text-sm text-gray-500">
              Ничего не найдено
            </div>
          )}
        </div>
      </Dropdown>
    </div>
  );
};

export default MarketplaceDropdown;
